import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { ROLES } from 'src/utils/MessagingUtils/shared-constants';
import {
  buildAndPostMessage,
  ChatInput,
  MessageRecipient,
} from 'src/utils/MessagingUtils/postMessageHelper';

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

export interface UseSuiteMessagesOrchestratorOptions {
  suite: any;
  property: any;
  postMessageToAPI: (data: any) => Promise<void>;
}

export interface UseSuiteMessagesOrchestratorReturn {
  user: any;
  onPostMessage: (chat: ChatInput) => Promise<void>;
}

export const useSuiteMessagesOrchestrator = ({
  suite,
  property,
  postMessageToAPI,
}: UseSuiteMessagesOrchestratorOptions): UseSuiteMessagesOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const resolveRecipient = (): MessageRecipient => {
    const userRole: string | undefined = user?.roles?.[0]?.role?.name;

    if (!userRole) {
      return { id: null, error: 'User role not found.' };
    }

    if (userRole === ROLES.PROPERTY_DEVELOPER) {
      const homeOwnerId: string | undefined = suite?.homeOwners?.id;

      if (!homeOwnerId || homeOwnerId === EMPTY_UUID) {
        return { id: null, error: 'No homeowner associated with this suite.' };
      }

      return { id: homeOwnerId };
    }

    const propertyManagerId: string | undefined = property?.propertyManager?.id;

    if (!propertyManagerId || propertyManagerId === EMPTY_UUID) {
      return {
        id: null,
        error: 'No property manager assigned to this property.',
      };
    }

    return { id: propertyManagerId };
  };

  const onPostMessage = async (chat: ChatInput): Promise<void> => {
    const recipient: MessageRecipient = resolveRecipient();
    const actionUrl: string = `app/suites/${suite.propertyId}&${suite.id}/activeTab=4`;

    await buildAndPostMessage({
      chat,
      user,
      recipient,
      referenceId: suite.id,
      referenceType: 'Suite',
      actionUrl,
      postMessageToAPI,
    });
  };

  return { user, onPostMessage };
};

export default useSuiteMessagesOrchestrator;
